
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Link } from "react-router-dom";
import { FormFields } from "@/components/shared/FormFields";
import { Button } from "@/components/ui/button";
import type { Database } from "@/integrations/supabase/types";

interface ExitIntentFormProps {
  isPro: boolean;
  onSuccess: () => void;
}

type WaitlistInsert = Database['public']['Tables']['waitlist']['Insert'];

export const ExitIntentForm = ({ isPro, onSuccess }: ExitIntentFormProps) => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    company: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  const handleFieldChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const insertData: WaitlistInsert = {
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        phone: formData.phone || null,
        company: formData.company || null,
        is_pro: isPro,
        gdpr_consent: true,
        status: 'pending',
        communication_preferences: { email: true, sms: false },
        source: typeof window !== 'undefined' ? window.location.pathname : '/',
        metadata: {
          browser: navigator.userAgent,
          language: navigator.language,
          timestamp: new Date().toISOString()
        }
      };

      const { error } = await supabase
        .from('waitlist')
        .insert(insertData);

      if (error) throw error;

      toast({
        title: "Inscription réussie !",
        description: isPro
          ? "Vos 6 mois offerts vous attendent !"
          : "La carte des pépites arrive bientôt !",
      });
      onSuccess();
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "Une erreur est survenue. Veuillez réessayer.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <FormFields
        {...formData}
        isPro={isPro}
        onChange={handleFieldChange}
      />

      <Button
        type="submit"
        className="w-full"
        disabled={isLoading}
      >
        {isLoading ? "Envoi en cours..." : "Je veux mon cadeau !"}
      </Button>

      <p className="text-sm text-gray-500 text-center">
        En vous inscrivant, vous acceptez notre{" "}
        <Link to="/privacy" className="text-primary hover:underline" target="_blank">
          politique de confidentialité
        </Link>
      </p>
    </form>
  );
};
